import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AsyncBoundary, Button, Card, Chip, KpiCard, Segmented, Tag } from '@kollez/design-system';
import { useWorkflows, type WorkflowTemplate } from '@/lib/queries';

/**
 * /workflows — list of Workflow templates the caller can see (v1.6 §7.6).
 *
 * Filter chips for status (live / draft / retired) and scope (GLOBAL / TENANT / PARTNER).
 * Each row shows name, version, hosting tenant, type, stage count. Clicking navigates to
 * /workflows/:id for the read-only detail page.
 *
 * Authoring UI lands in Step 24. Today this surface is the "what templates exist for me"
 * picker that TPOs + partners consult before deciding whether to author or reuse.
 */

type StatusFilter = 'all' | 'live' | 'draft' | 'retired';
type ScopeFilter = 'all' | 'GLOBAL' | 'TENANT' | 'PARTNER';

export function WorkflowsPage() {
  const [status, setStatus] = useState<StatusFilter>('live');
  const [scope, setScope] = useState<ScopeFilter>('all');
  const q = useWorkflows({
    ...(status !== 'all' ? { status } : {}),
    ...(scope !== 'all' ? { scope } : {}),
  });
  const rows = q.data?.rows ?? [];

  const liveCount = rows.filter((r) => r.status === 'live').length;
  const draftCount = rows.filter((r) => r.status === 'draft').length;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">v1.6 §7.6 · Workflow templates</p>
          <h1 className="h-page">Workflows</h1>
          <p className="subhead">
            Every Opportunity runs a Workflow — stages carry authoring hats, reversibility class, gates,
            monetization, and badge emit/satisfy rules. Kollez owns GLOBAL templates. Tenants + partners
            author their own.
          </p>
        </div>
        <div className="ph-actions">
          <Link to="/workflows/new"><Button variant="primary">New workflow</Button></Link>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <KpiCard label="Templates visible" value={rows.length} />
        <KpiCard label="Live" value={liveCount} />
        <KpiCard label="Drafts" value={draftCount} />
      </div>

      <Card padding="md">
        <div className="flex flex-wrap items-center gap-3">
          <Segmented
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            options={[
              { value: 'live', label: 'Live' },
              { value: 'draft', label: 'Draft' },
              { value: 'retired', label: 'Retired' },
              { value: 'all', label: 'All' },
            ]}
          />
          <div className="flex items-center gap-2">
            <span className="cell-sub">Scope</span>
            <Segmented
              value={scope}
              onChange={(v) => setScope(v as ScopeFilter)}
              options={[
                { value: 'all', label: 'Any' },
                { value: 'GLOBAL', label: 'Global' },
                { value: 'TENANT', label: 'Tenant' },
                { value: 'PARTNER', label: 'Partner' },
              ]}
            />
          </div>
        </div>
      </Card>

      <AsyncBoundary
        kind="table"
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && rows.length === 0}
        emptyTitle="No templates match"
        emptyHint="Loosen the filters or ask platform ops to publish a canonical template for your tenant."
      >
        <div className="grid gap-2">
          {rows.map((w) => <WorkflowRow key={w._id} workflow={w} />)}
        </div>
      </AsyncBoundary>
    </div>
  );
}

function WorkflowRow({ workflow }: { workflow: WorkflowTemplate }) {
  const hostedByName =
    typeof workflow.hostedBy === 'object' ? workflow.hostedBy?.name ?? '—' : '—';
  return (
    <Link to={`/workflows/${workflow._id}`} className="cell block" style={{ padding: '12px 14px' }}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <b>{workflow.name}</b>
            <Chip tone={workflow.status === 'live' ? 'success' : workflow.status === 'draft' ? 'warning' : 'default'}>
              {workflow.status}
            </Chip>
            <Tag pressed>v{workflow.version}</Tag>
          </div>
          <div className="cell-sub">
            {workflow.scope} · hosted by {hostedByName} · type {workflow.type} · {workflow.stages.length} stages
          </div>
        </div>
        <div className="cell-sub">→</div>
      </div>
    </Link>
  );
}
